import React, { useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/joy';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requiresOrganization?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiresOrganization = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Preserve the current path for post-login redirect
      const currentPath = window.location.pathname + window.location.search;
      const loginUrl = `/login/?next=${encodeURIComponent(currentPath)}`;
      
      // Redirect to Django login page
      window.location.href = loginUrl;
      return;
    }
    
    // Handle affiliate-only users (no organization)
    if (!loading && isAuthenticated && user) {
      const isAffiliateOnly = user.is_affiliate && !user.organization;
      const currentPath = location.pathname;
      
      // List of paths accessible to affiliate-only users
      const affiliateOnlyPaths = [
        '/affiliate-dashboard',
        '/settings/affiliate',
        '/logout'
      ];
      
      if (isAffiliateOnly) {
        // Check if current path is allowed for affiliate-only users
        const isAllowedPath = affiliateOnlyPaths.some(path => currentPath.startsWith(path));
        
        if (!isAllowedPath) {
          // Redirect affiliate-only users to their dashboard if accessing restricted pages
          navigate('/affiliate-dashboard', { replace: true });
        }
      } else if (requiresOrganization && !user.organization) {
        // If route requires organization but user doesn't have one, redirect to dashboard
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, loading, user, location.pathname, navigate, requiresOrganization]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-md" color="neutral">
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Show a brief message before redirect
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <Typography level="body-md" color="neutral">
          Redirecting to login...
        </Typography>
      </Box>
    );
  }

  return children ? <>{children}</> : <Outlet />;
};